import React, { useState, useEffect, useCallback } from "react";
import { Button, Col, Input, Row, Select, Tag, message } from "antd";
import {
  SearchOutlined,
  DeleteOutlined,
  FormOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  AuditOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { ticketAPIWithErrorHandling, ticketHelpers } from "../../../api/ticket/ticket";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import StatCard from "../../../components/common/StatCard";
import { confirmDelete } from "../../../components/common/confirmDialog";
import { actionColumnDef } from "../../../components/common/RowActions";
import ListToolbar, {
  selectVisibleColumns,
  useListToolbar,
} from "../../../components/common/ListToolbar";
import { neutral, typography } from "../../../theme/tokens";
import { statusTag, STATUS_OPTIONS, staffRequesterOf, ticketCode } from "./ticketMeta";
import TicketReviewDrawer from "./TicketReviewDrawer";

const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = [
  "created_at", "code", "customer", "subtype", "requested_value", "status",
];

/** Dòng phụ dưới con số của thẻ — chữ xám nhỏ, không tô màu theo giá trị. */
const statExtra = (text) => (
  <div style={{ marginTop: 6, fontSize: typography.size.sm, color: neutral[500] }}>{text}</div>
);

/**
 * Danh sách yêu cầu hỗ trợ.
 *
 * Cột "Nội dung" (subtype) là bắt buộc: không có nó thì nhìn danh sách không
 * biết vé nào về việc gì, phải mở từng vé ra xem — đúng lỗi của mấy bảng ticket
 * chỉ hiện ID / Loại / Người tạo / Trạng thái.
 */
export default function TicketTable() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [subtypeFilter, setSubtypeFilter] = useState("");

  /** Danh mục nội dung lấy từ backend, không chép cứng ở frontend. */
  const [subtypes, setSubtypes] = useState([]);

  /** Mini dashboard — đếm toàn bộ vé của đơn vị, không theo bộ lọc đang chọn. */
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [selectedRecord, setSelectedRecord] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { visibleFilters, setVisibleFilters, visibleColumns, setVisibleColumns } = useListToolbar({
    defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
  });

  const fetchTickets = useCallback(
    async (page = 1, pageSize = 10, search = "", status = "", subtype = "") => {
      setLoading(true);
      try {
        const response = await ticketAPIWithErrorHandling.getTickets({
          page,
          limit: pageSize,
          search: search.trim(),
          status,
          subtype,
        });
        const { tickets: data, pagination: pg } = ticketHelpers.extractTicketList(response);
        setTickets(data);
        setPagination({ current: pg.current, pageSize: pg.pageSize, total: pg.total });
      } catch (error) {
        message.error(error.message || "Không thể tải danh sách yêu cầu hỗ trợ");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      setStats(ticketHelpers.extractTicketData(await ticketAPIWithErrorHandling.getStats()));
    } catch {
      // Thẻ số liệu hỏng không được chặn danh sách — để trống, danh sách vẫn dùng được.
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, [fetchTickets, fetchStats]);

  useEffect(() => {
    ticketAPIWithErrorHandling
      .getMeta()
      .then((res) => setSubtypes(ticketHelpers.extractTicketData(res)))
      .catch(() => setSubtypes([]));
  }, []);

  const subtypeLabel = (value) =>
    subtypes.find((item) => item.value === value)?.label || value || "—";

  /** Xử lý / xóa vé làm đổi số liệu, nên tải lại cả thẻ lẫn danh sách. */
  const reload = () => {
    fetchStats();
    fetchTickets(pagination.current, pagination.pageSize, searchText, statusFilter, subtypeFilter);
  };

  const handleReset = () => {
    setSearchText("");
    setStatusFilter("");
    setSubtypeFilter("");
    fetchTickets(1, pagination.pageSize, "", "", "");
  };

  const handleDelete = async (record) => {
    if (await confirmDelete({ entity: "yêu cầu hỗ trợ" })) {
      try {
        await ticketAPIWithErrorHandling.deleteTicket(record.id);
        message.success("Xóa yêu cầu hỗ trợ thành công");
        reload();
      } catch (error) {
        message.error(error.message);
      }
    }
  };

  const openReview = (record) => {
    setSelectedRecord(record);
    setDrawerOpen(true);
  };

  const columnDefs = [
    {
      key: "created_at", label: "Ngày gửi",
      column: {
        title: "Ngày gửi", dataIndex: "created_at", key: "created_at", width: 140,
        render: (date) => dayjs(date).format("DD/MM/YYYY HH:mm"),
      },
    },
    {
      key: "code", label: "Mã vé",
      column: {
        title: "Mã vé", dataIndex: "id", key: "code", width: 100,
        render: (id) => <span className="font-mono text-gray-700">{ticketCode(id)}</span>,
      },
    },
    {
      key: "customer", label: "Cư dân",
      column: {
        title: "Cư dân", dataIndex: "customer", key: "customer", width: 220,
        render: (customer, record) => {
          const staff = staffRequesterOf(record);
          return (
            <div className="flex flex-col gap-1">
              <span className="font-semibold text-gray-800">{customer?.name || "—"}</span>
              <div className="text-xs text-gray-500 flex items-center gap-2">
                <span>{customer?.code}</span>
                {/* Vé trưởng khu phố gửi thì `record.phone` là số cán bộ, không phải của dân. */}
                <span>{customer?.phone || (staff ? null : record.phone)}</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {record.organization?.name && (
                  <Tag color="default" className="m-0 w-fit text-[10px] px-1">
                    {record.organization.name}
                  </Tag>
                )}
                {staff && (
                  <Tag color="geekblue" className="m-0 w-fit text-[10px] px-1">
                    Trưởng khu phố: {staff.name}
                  </Tag>
                )}
              </div>
            </div>
          );
        },
      },
    },
    {
      key: "subtype", label: "Nội dung",
      column: {
        title: "Nội dung", dataIndex: "subtype", key: "subtype", width: 200,
        render: (value) => subtypeLabel(value),
      },
    },
    {
      key: "requested_value", label: "Đề nghị sửa thành",
      column: {
        title: "Đề nghị sửa thành", dataIndex: "requested_value", key: "requested_value",
        ellipsis: true,
        render: (text, record) => (
          <Button
            type="link"
            onClick={() => openReview(record)}
            className="text-blue-600 hover:text-blue-800 font-medium"
            style={{ padding: 0, height: "auto" }}
          >
            {text || "(không có)"}
          </Button>
        ),
      },
    },
    {
      key: "status", label: "Trạng thái",
      column: {
        title: "Trạng thái", dataIndex: "status", key: "status", width: 140,
        render: (status) => statusTag(status),
      },
    },
    actionColumnDef((record) => [
      { key: "review", label: "Xem & xử lý", icon: <FormOutlined />, tone: "warning", onClick: () => openReview(record) },
      { key: "delete", label: "Xóa", icon: <DeleteOutlined />, tone: "danger", onClick: () => handleDelete(record) },
    ]),
  ];

  const filterDefs = [
    {
      key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <Input.Search
          placeholder="Mã vé, tên cư dân, nội dung đề nghị..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={(value) => {
            setSearchText(value);
            fetchTickets(1, pagination.pageSize, value, statusFilter, subtypeFilter);
          }}
          enterButton={<SearchOutlined />}
          allowClear
        />
      ),
    },
    {
      key: "status", label: "Trạng thái", span: { xs: 24, sm: 12, md: 5 },
      field: (
        <Select
          style={{ width: "100%" }}
          placeholder="Lọc theo trạng thái"
          value={statusFilter || undefined}
          onChange={(value) => {
            const next = value || "";
            setStatusFilter(next);
            fetchTickets(1, pagination.pageSize, searchText, next, subtypeFilter);
          }}
          options={STATUS_OPTIONS}
          allowClear
        />
      ),
    },
    {
      key: "subtype", label: "Nội dung", span: { xs: 24, sm: 12, md: 6 },
      field: (
        <Select
          style={{ width: "100%" }}
          placeholder="Lọc theo nội dung"
          value={subtypeFilter || undefined}
          onChange={(value) => {
            const next = value || "";
            setSubtypeFilter(next);
            fetchTickets(1, pagination.pageSize, searchText, statusFilter, next);
          }}
          options={subtypes.map((item) => ({ value: item.value, label: item.label }))}
          allowClear
        />
      ),
    },
  ];

  const columns = selectVisibleColumns(columnDefs, visibleColumns);
  const hasActiveFilter = !!(searchText || statusFilter || subtypeFilter);

  const byStatus = stats?.by_status || {};
  const total = stats?.total ?? 0;
  const byStaff = stats?.by_staff ?? 0;

  return (
    <BrandedPageLayout
      title="Yêu cầu hỗ trợ"
      subtitle="Cư dân hoặc trưởng khu phố đề nghị đính chính thông tin hồ sơ. Xác minh rồi cập nhật hồ sơ trước khi đánh dấu hoàn thành."
      breadcrumbItems={[{ title: "Yêu cầu hỗ trợ" }]}
    >
      {/* Mini dashboard — toàn bộ vé của đơn vị, không theo bộ lọc bên dưới. */}
      <Row gutter={[16, 16]} className="mb-4">
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<FileTextOutlined />}
            tone="brand"
            label="Tổng yêu cầu"
            value={total}
            loading={statsLoading && !stats}
            extra={statExtra(`Cư dân gửi ${total - byStaff} · Trưởng khu phố gửi ${byStaff}`)}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<ClockCircleOutlined />}
            tone="warning"
            label="Chờ tiếp nhận"
            value={byStatus.received ?? 0}
            loading={statsLoading && !stats}
            extra={statExtra("Mới gửi, chưa ai xử lý")}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<AuditOutlined />}
            tone="info"
            label="Đang xác minh"
            value={byStatus.processing ?? 0}
            loading={statsLoading && !stats}
            extra={statExtra("Đang đối chiếu giấy tờ")}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<CheckCircleOutlined />}
            tone="success"
            label="Hoàn thành"
            value={byStatus.completed ?? 0}
            loading={statsLoading && !stats}
            extra={statExtra(`Từ chối ${byStatus.rejected ?? 0} · Đã hủy ${byStatus.cancelled ?? 0}`)}
          />
        </Col>
      </Row>

      <ListToolbar
        filterDefs={filterDefs}
        visibleFilters={visibleFilters}
        onChangeVisibleFilters={setVisibleFilters}
        columnDefs={columnDefs}
        visibleColumns={visibleColumns}
        onChangeVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        hasActiveFilter={hasActiveFilter}
        onClearFilter={handleReset}
        onReload={reload}
      />

      <ListTableCard
        columns={columns}
        dataSource={tickets}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
        }}
        onChange={(config) =>
          fetchTickets(config.current, config.pageSize, searchText, statusFilter, subtypeFilter)
        }
        emptyText="Chưa có yêu cầu hỗ trợ nào"
      />

      <TicketReviewDrawer
        open={drawerOpen}
        ticket={selectedRecord}
        subtypeLabel={subtypeLabel}
        onClose={() => setDrawerOpen(false)}
        onDone={() => {
          setDrawerOpen(false);
          reload();
        }}
      />
    </BrandedPageLayout>
  );
}
